import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

// Channels beyond the widget and email: connecting any of these counts the
// "messaging channels" step as done.
const MESSAGING_TYPES = [
  INBOX_TYPES.WHATSAPP,
  INBOX_TYPES.FB,
  INBOX_TYPES.INSTAGRAM,
  INBOX_TYPES.TIKTOK,
  INBOX_TYPES.TWITTER,
  INBOX_TYPES.TWILIO,
  INBOX_TYPES.TELEGRAM,
  INBOX_TYPES.LINE,
  INBOX_TYPES.SMS,
  INBOX_TYPES.API,
];

/**
 * The five setup steps, each derived from workspace state the dashboard
 * already holds - no stored onboarding flags. Same derivation approach as the
 * former Overview SetupPanel, extended with email, messaging channels, and
 * the AI agent.
 */
export function useSetupSteps() {
  const { t } = useI18n();
  const store = useStore();

  const inboxes = useMapGetter('inboxes/getInboxes');
  const agents = useMapGetter('agents/getAgents');
  const assistants = useMapGetter('captainAssistants/getRecords');

  const fetchSetupData = () => {
    store.dispatch('inboxes/get');
    store.dispatch('agents/get');
    store.dispatch('captainAssistants/get');
  };

  const hasType = types =>
    inboxes.value.some(inbox => types.includes(inbox.channel_type));

  const steps = computed(() => [
    {
      key: 'widget',
      title: t('SETUP.STEPS.WIDGET.TITLE'),
      description: t('SETUP.STEPS.WIDGET.DESCRIPTION'),
      cta: t('SETUP.STEPS.WIDGET.CTA'),
      done: hasType([INBOX_TYPES.WEB]),
      to: { name: 'settings_inbox_new' },
    },
    {
      key: 'team',
      title: t('SETUP.STEPS.TEAM.TITLE'),
      description: t('SETUP.STEPS.TEAM.DESCRIPTION'),
      cta: t('SETUP.STEPS.TEAM.CTA'),
      done: agents.value.length > 1,
      to: { name: 'agent_list' },
    },
    {
      key: 'email',
      title: t('SETUP.STEPS.EMAIL.TITLE'),
      description: t('SETUP.STEPS.EMAIL.DESCRIPTION'),
      cta: t('SETUP.STEPS.EMAIL.CTA'),
      done: hasType([INBOX_TYPES.EMAIL]),
      to: { name: 'settings_inbox_new' },
    },
    {
      key: 'channels',
      title: t('SETUP.STEPS.CHANNELS.TITLE'),
      description: t('SETUP.STEPS.CHANNELS.DESCRIPTION'),
      cta: t('SETUP.STEPS.CHANNELS.CTA'),
      done: hasType(MESSAGING_TYPES),
      to: { name: 'settings_inbox_new' },
    },
    {
      key: 'ai',
      title: t('SETUP.STEPS.AI.TITLE'),
      description: t('SETUP.STEPS.AI.DESCRIPTION'),
      cta: t('SETUP.STEPS.AI.CTA'),
      done: assistants.value.length > 0,
      to: {
        name: 'captain_assistants_index',
        params: { navigationPath: 'captain_assistants_overview_index' },
      },
    },
  ]);

  const doneCount = computed(
    () => steps.value.filter(step => step.done).length
  );
  const isComplete = computed(() => doneCount.value === steps.value.length);

  return { steps, doneCount, isComplete, fetchSetupData };
}
